import { ApDocumentType, ApNoteAdjustment, ApNotePriceMode, ApNoteReason, GENERAL_ISV_RATE } from '../models/ap-document/ap-document';

/** What a Credit/Debit Note line will come to once saved (#6324). */
export interface ApNoteLinePreview {
  quantity: number;
  lineTotal: number;
  taxAmount: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** The ISV inside a gross amount — the extraction the API (and ComputeLineTotals) uses, net rounded first. */
export const embeddedTax = (gross: number, rate: number): number => (rate > 0 ? round2(gross - round2(gross / (1 + rate))) : 0);

/**
 * Previews one note line from its adjustment, the same way ApNoteLines.Compute derives it on the API, so the
 * form's totals match what posts. The API is still the authority: this only previews, and returns null for an
 * input the API would refuse (nothing entered, a price or rate moving the wrong way) so the row shows nothing
 * rather than a figure that will never post. Prices and amounts the user types are NET of ISV.
 */
export function previewNoteLine(type: ApDocumentType, reason: ApNoteReason | null, adjustment: ApNoteAdjustment): ApNoteLinePreview | null {
  const credit = type === ApDocumentType.CreditNote;
  const sourceQuantity = adjustment.sourceQuantity ?? 0;
  const sourceNetUnit = sourceQuantity > 0 ? ((adjustment.sourceLineTotal ?? 0) - (adjustment.sourceTaxAmount ?? 0)) / sourceQuantity : 0;
  const sourceRate = adjustment.sourceTaxRate ?? 0;

  switch (reason) {
    case ApNoteReason.Quantity: {
      const units = adjustment.quantity ?? 0;
      if (units <= 0) return null;
      const lineTotal = round2(units * (adjustment.sourceUnitPrice ?? 0));
      return { quantity: units, lineTotal, taxAmount: embeddedTax(lineTotal, sourceRate) };
    }
    case ApNoteReason.PriceOrValue: {
      let units = 1;
      let net: number;
      if (adjustment.priceMode === ApNotePriceMode.CorrectUnitPrice) {
        const correct = adjustment.correctUnitPrice;
        if (correct == null || correct < 0) return null;
        units = adjustment.quantity ?? sourceQuantity;
        if (units <= 0 || units > sourceQuantity) return null;
        if (credit ? correct >= sourceNetUnit : correct <= sourceNetUnit) return null;
        net = round2(units * Math.abs(sourceNetUnit - correct));
      } else if (adjustment.priceMode === ApNotePriceMode.FixedAmount) {
        net = round2(adjustment.amount ?? 0);
      } else {
        return null;
      }
      if (net <= 0) return null;
      const tax = adjustment.adjustsTax ? round2(net * sourceRate) : 0;
      return { quantity: units, lineTotal: round2(net + tax), taxAmount: tax };
    }
    case ApNoteReason.Tax: {
      const correctRate = adjustment.correctTaxRate;
      if (correctRate == null || correctRate < 0 || correctRate >= 1) return null;
      if (credit ? correctRate >= sourceRate : correctRate <= sourceRate) return null;
      const base = (adjustment.sourceLineTotal ?? 0) - (adjustment.sourceTaxAmount ?? 0);
      const tax = round2(base * Math.abs(sourceRate - correctRate));
      return tax > 0 ? { quantity: 1, lineTotal: tax, taxAmount: tax } : null;
    }
    case ApNoteReason.Financial: {
      const net = round2(adjustment.amount ?? 0);
      if (net <= 0) return null;
      const tax = adjustment.adjustsTax ? round2(net * GENERAL_ISV_RATE) : 0;
      return { quantity: 1, lineTotal: round2(net + tax), taxAmount: tax };
    }
    default:
      return null;
  }
}

/**
 * How a Credit Note will split when it posts (#6324): what its original still owes is settled, the rest is the
 * provider's open credit ("saldo a favor"). Mirrors ApNoteLines.CreditSplit.
 */
export function previewCreditSplit(noteTotal: number, originalBalance: number | null): { applied: number; providerCredit: number } {
  const applied = originalBalance == null ? 0 : round2(Math.min(noteTotal, Math.max(0, originalBalance)));
  return { applied, providerCredit: round2(noteTotal - applied) };
}
